import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { SegmentedControl } from '@client/components/_ui/data-entry/SegmentedControl';
import { RecordCard, RecordTableToolbar, recordTablePagination } from '@client/components/_ui/display/RecordPrimitives';
import { Confirm } from '@client/components/_ui/feedback/Confirm';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { message } from '@client/components/_ui/feedback/message';
import { formatSupplierArea } from '@client/components/repair-networks/CoverageSummary';
import { AddRepairerPreferencesDrawer } from '@client/components/repairer-preferences/AddRepairerPreferencesDrawer';
import {
    type ClientPreferenceRow,
    EditRepairerPreferenceModal,
} from '@client/components/repairer-preferences/EditRepairerPreferenceModal';
import {
    useCanManageRepairerPreferences,
    useCanReadRepairerPreferences,
} from '@client/components/repairer-preferences/preference-access';
import { RepairerPreferenceTag } from '@client/components/repairer-preferences/RepairerPreferenceTag';
import { RepairerCapabilityChips } from '@client/components/suppliers/RepairerCapabilityChips';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createColumnHelper, PrismaTable, TableEmptyState, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { type RouterInputs, trpc } from '@client/trpc/client';
import type { RepairerCapabilityCode } from '@shared/constants/business/repairer-capability';
import { keepPreviousData, skipToken } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Input, Select, Tag } from 'antd';
import { Info } from 'lucide-react';
import { useEffect, useState } from 'react';
import { isConsolidatorRecord } from './ClientRecordHeader';
import { useClientRecord } from './client-record-data';

export const Route = createFileRoute('/console/$orgId/configuration/fleet/client-database/details/$id/repairers')({
    component: RouteComponent,
});

function RouteComponent() {
    const { orgId, id } = Route.useParams();
    return (
        <PageController route={Route}>
            <RepairersTab key={`${orgId}:${id}`} />
        </PageController>
    );
}

const SUPPLIER_DETAILS_ROUTE = '/console/$orgId/configuration/vendor-management/suppliers/list/details/$id';

type PreferencesInput = RouterInputs['repairerPreference']['listClientRepairerPreferences'];
type PreferencesQueryParams = Omit<PreferencesInput, 'fleetClientId'>;
type PreferenceScope = 'all' | NonNullable<PreferencesInput['preference']>;

const columnHelper = createColumnHelper<ClientPreferenceRow, NonNullable<PreferencesInput['orderBy']>>();

/**
 * The client's preferred and excluded repairers (CUR-1104), applied on top of its repair network.
 * Excluded is absolute; preferred ranks first among repairers that are already eligible.
 */
function RepairersTab() {
    const orgId = useOrgId();
    const navigate = useNavigate();
    const { trpcUtils } = useTrpc();
    const { formatDate } = useFormatter();
    const { id } = Route.useParams();
    const { data: client } = useClientRecord(id);
    const canRead = useCanReadRepairerPreferences();
    const canEdit = useCanManageRepairerPreferences();

    const [params, setParams] = useState<PreferencesQueryParams>({
        limit: 20,
        offset: 0,
        orderBy: { name: 'asc' },
    });
    const [searchText, setSearchText] = useState('');
    const [editing, setEditing] = useState<ClientPreferenceRow | null>(null);
    const [isAddOpen, setIsAddOpen] = useState(false);

    const isClient = client ? !isConsolidatorRecord(client) : false;
    const preferencesQuery = trpc.repairerPreference.listClientRepairerPreferences.useQuery(
        isClient && canRead ? { ...params, fleetClientId: id } : skipToken,
        // Keep the last page and its counts on screen while the next one loads; without this
        // every sort, filter or page change flashes the counts to 0 and drops the pager.
        { placeholderData: keepPreviousData },
    );
    const remove = trpc.repairerPreference.removeClientRepairerPreference.useMutation();

    const counts = preferencesQuery.data?.counts;
    const network = preferencesQuery.data?.network ?? null;
    const totalPreferences = counts ? counts.preferred + counts.excluded : 0;
    const hasActiveFilters = !!params.search || !!params.preference || params.inNetwork !== undefined;
    const scope: PreferenceScope = params.preference ?? 'all';

    const invalidate = () => {
        void trpcUtils.repairerPreference.invalidate();
    };
    const setFilter = (patch: Partial<PreferencesQueryParams>) =>
        setParams((prev) => ({ ...prev, ...patch, offset: 0 }));

    // Removing the last row of the last page leaves an offset past the end. Step back to the new
    // last page, or the table would claim there are no preferences while the counts say otherwise.
    const loadedTotal = preferencesQuery.data?.total;
    const loadedRows = preferencesQuery.data?.rows.length;
    useEffect(() => {
        if (preferencesQuery.isPlaceholderData || loadedTotal === undefined || loadedRows !== 0) return;
        setParams((prev) => {
            const offset = prev.offset ?? 0;
            const limit = prev.limit ?? 20;
            if (offset === 0 || loadedTotal === 0) return prev;
            return { ...prev, offset: Math.max(0, (Math.ceil(loadedTotal / limit) - 1) * limit) };
        });
    }, [loadedTotal, loadedRows, preferencesQuery.isPlaceholderData]);

    const tableProps = usePrismaTable({
        rowKey: (row) => row.supplierId.toString(),
        data: preferencesQuery.data?.rows || [],
        total: preferencesQuery.data?.total || 0,
        loading: preferencesQuery.isFetching,
        queryParams: params,
        onParamsChange: setParams,
        pagination: recordTablePagination,
        emptyContent: (
            <TableEmptyState
                hasActiveFilters={hasActiveFilters}
                noRecordsTitle="No repairer preferences"
                noRecordsDescription={`Every repairer in ${network?.name ?? "this client's network"} that covers the vehicle's location is offered equally, nearest first. ${canEdit ? "Add a preference to change the order or to keep a repairer off this client's claims." : 'Only someone who can manage suppliers can add a preference.'}`}
                noMatchesTitle="No repairers match"
                noMatchesDescription="Try another name, suburb or postcode, or clear the filters."
            />
        ),
        columns: [
            columnHelper.accessor('supplier', {
                id: 'repairer',
                header: 'Repairer',
                sortable: 'name',
                cell: ({ row }) => (
                    <div className="flex min-w-0 flex-col">
                        <span className="font-semibold">{row.supplier.name}</span>
                        <span className="text-subtle-foreground text-xs">{formatSupplierArea(row.supplier)}</span>
                    </div>
                ),
            }),
            columnHelper.accessor('preference', {
                id: 'preference',
                header: 'Preference',
                sortable: 'preference',
                width: 120,
                cell: ({ row }) => <RepairerPreferenceTag preference={row.preference} />,
            }),
            columnHelper.display({
                id: 'network',
                header: 'Network',
                width: 140,
                cell: ({ row }) =>
                    row.inNetwork ? (
                        <span>{network?.name}</span>
                    ) : (
                        <Tag variant="outlined" color="warning" className="m-0">
                            Not in network
                        </Tag>
                    ),
            }),
            columnHelper.display({
                id: 'capability',
                header: 'Capability',
                // Capability labels are long; a fixed width wraps the chips instead of pushing
                // the note and date off the row.
                width: 210,
                cell: ({ row }) => (
                    <RepairerCapabilityChips codes={row.supplier.capabilityCodes as RepairerCapabilityCode[]} />
                ),
            }),
            columnHelper.display({
                id: 'note',
                header: 'Note',
                width: 190,
                cell: ({ row }) =>
                    row.note ? (
                        <span className="block max-w-44 truncate" title={row.note}>
                            {row.note}
                        </span>
                    ) : (
                        <span className="text-subtle-foreground">—</span>
                    ),
            }),
            columnHelper.accessor('createdAt', {
                id: 'since',
                header: 'Since',
                sortable: 'createdAt',
                width: 105,
                cell: ({ row }) => (
                    <span className="whitespace-nowrap text-subtle-foreground tabular-nums">
                        {formatDate(row.createdAt)}
                    </span>
                ),
            }),
            columnHelper.actions({
                width: 1,
                cell: ({ row }) =>
                    canEdit ? (
                        <div
                            className="flex items-center justify-end gap-1"
                            onClick={(event) => event.stopPropagation()}
                        >
                            <Button type="link" size="small" onClick={() => setEditing(row)}>
                                Edit
                            </Button>
                            <Confirm
                                title={`Remove the preference for ${row.supplier.name}?`}
                                description={
                                    row.preference === 'excluded'
                                        ? "The repairer can be offered on this client's claims again, through its network."
                                        : "The repairer is ranked like any other in this client's network."
                                }
                                okText="Remove"
                                okDanger
                                onOk={async () => {
                                    try {
                                        await remove.mutateAsync({ fleetClientId: id, supplierId: row.supplierId });
                                        message.success('Preference removed.');
                                        invalidate();
                                    } catch (error) {
                                        errorMessage.show(error);
                                    }
                                }}
                            >
                                <Button type="link" size="small" danger>
                                    Remove
                                </Button>
                            </Confirm>
                        </div>
                    ) : null,
            }),
        ],
    });

    if (!client || !isClient || !canRead) return null;

    return (
        <RecordCard
            padded={false}
            footer={
                <span className="flex items-start gap-2">
                    <Info size={14} className="mt-0.5 flex-none" aria-hidden />
                    Excluded is absolute: the repairer is never offered on this client's claims, whatever its network or
                    distance. Preferred repairers lead the eligible list; a preferred repairer outside the network is
                    not offered until it joins.
                </span>
            }
        >
            <RecordTableToolbar>
                <Input.Search
                    placeholder="Search by repairer, suburb or postcode"
                    allowClear
                    className="w-72"
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    onSearch={(value) => setFilter({ search: value.trim() || undefined })}
                />
                <SegmentedControl<PreferenceScope>
                    aria-label="Preference"
                    size="small"
                    value={scope}
                    onChange={(value) => setFilter({ preference: value === 'all' ? undefined : value })}
                    options={[
                        { value: 'all', label: 'All', badge: <ScopeCount>{totalPreferences}</ScopeCount> },
                        {
                            value: 'preferred',
                            label: 'Preferred',
                            badge: <ScopeCount>{counts?.preferred ?? 0}</ScopeCount>,
                        },
                        {
                            value: 'excluded',
                            label: 'Excluded',
                            badge: <ScopeCount>{counts?.excluded ?? 0}</ScopeCount>,
                        },
                    ]}
                />
                <Select
                    aria-label="In network"
                    placeholder="In network"
                    allowClear
                    className="w-40"
                    value={params.inNetwork === undefined ? undefined : params.inNetwork ? 'in' : 'out'}
                    onChange={(value: 'in' | 'out' | undefined) =>
                        setFilter({ inNetwork: value === undefined ? undefined : value === 'in' })
                    }
                    options={[
                        { value: 'in', label: 'In network' },
                        { value: 'out', label: 'Not in network' },
                    ]}
                />
                <span className="ml-auto whitespace-nowrap text-subtle-foreground text-xs">
                    {counts
                        ? `${totalPreferences} ${totalPreferences === 1 ? 'repairer' : 'repairers'}${counts.outsideNetwork ? ` · ${counts.outsideNetwork} outside the network` : ''}`
                        : ''}
                </span>
                {canEdit ? (
                    <Button.Add label="Add repairers" variant="primary" onClick={() => setIsAddOpen(true)} />
                ) : null}
            </RecordTableToolbar>
            {preferencesQuery.error ? (
                <div className="p-4">
                    <InlineQueryError
                        compact
                        error={preferencesQuery.error}
                        onRetry={() => void preferencesQuery.refetch()}
                    />
                </div>
            ) : (
                <PrismaTable
                    {...tableProps}
                    onRow={(row) => ({
                        className: 'cursor-pointer',
                        onClick(event) {
                            navigate({ to: SUPPLIER_DETAILS_ROUTE, params: { orgId, id: row.supplierId } }, event);
                        },
                    })}
                />
            )}

            <EditRepairerPreferenceModal
                row={editing}
                clientName={client.name}
                onClose={() => setEditing(null)}
                onSaved={() => setEditing(null)}
            />
            <AddRepairerPreferencesDrawer
                open={isAddOpen}
                fleetClientId={id}
                clientName={client.name}
                onClose={() => setIsAddOpen(false)}
                onAdded={() => setIsAddOpen(false)}
            />
        </RecordCard>
    );
}

function ScopeCount({ children }: { children: number }) {
    return <span className="font-semibold text-2xs text-subtle-foreground tabular-nums">{children}</span>;
}
